/**
 * Лента заметок встречи (DEV-1658, экран «Встречи» — DEV-1973).
 *
 * Стор, а не состояние экрана: ту же ленту читает форма задачи, открытая из
 * заметки в соседней вкладке, и переключение встречи в списке слева не должно
 * терять того, что уже загружено.
 */
import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'

import { useAssistantStore, type PanelCall, type PanelItem } from '@/stores/assistant'

import { createNote, deleteNote, listNotes, updateNote, type Note } from './notes'

/** Инструменты ассистента, из которых выходят задачи */
const CREATE_TOOLS = new Set(['create_issue', 'create_issues_batch'])

/** Отработавшие вызовы создания задач в ленте панели */
const createCalls = (items: PanelItem[]): PanelCall[] =>
  items.filter(
    (item): item is PanelCall =>
      item.kind === 'call' && item.state === 'done' && CREATE_TOOLS.has(item.name),
  )

/**
 * Ключи задач в выдаче инструмента.
 *
 * Формы две: пачка отдаёт список задач, одиночный вызов — одну задачу, и
 * читать надо обе. Одну задачу из мысли заводят чаще, чем пачку, и ровно этот
 * случай раньше проходил мимо пометки (DEV-2025).
 */
const issueKeysOf = (result: unknown): string[] => {
  const payload = result as { issues?: { key?: string }[]; issue?: { key?: string } } | null
  const issues = payload?.issues ?? (payload?.issue ? [payload.issue] : [])

  return issues.map((issue) => issue?.key ?? '').filter(Boolean)
}

export const useNotesStore = defineStore('meeting-notes', () => {
  const items = ref<Note[]>([])
  const isLoading = ref(false)
  const isLoaded = ref(false)
  const error = ref<unknown>(null)

  /**
   * Заметка, которую сейчас разбирает ассистент.
   *
   * Признак живёт здесь, а не в компоненте: разбор идёт в панели ассистента, а
   * она переживает и уход с экрана, и смену режима. Без него применённый
   * черновик не к чему было бы привязать — панель знает только про созданные
   * задачи, но не про то, из какой мысли они выросли.
   */
  const parsingId = ref('')

  /**
   * Встреча, чью ленту показываем. Пусто — обычная лента: свои личные заметки
   * плюс заметки всех встреч, где человек был (DEV-1947).
   */
  const sessionId = ref('')

  /**
   * Сколько заметок всего против того, сколько влезло на экран.
   *
   * Длинный протокол обрезается по лимиту ленты, и молчать об этом нельзя:
   * человек читал бы кусок встречи, считая его целым.
   */
  const total = ref(0)

  const isTruncated = computed(() => total.value > items.value.length)

  const fresh = computed(() => items.value.filter((note) => !note.processedAt))

  /**
   * Номер запроса ленты.
   *
   * Переключение встречи и приход события идут вперемешку, а ответы
   * возвращаются в своём порядке: без номера ответ по старой сессии перерисовал
   * бы ленту новой — чужими заметками на экране.
   */
  let request = 0

  const load = async () => {
    isLoading.value = true

    const own = (request += 1)
    const wanted = sessionId.value

    try {
      const loaded = await listNotes(wanted)

      if (own !== request) return

      items.value = loaded.items
      total.value = loaded.total
      isLoaded.value = true
      error.value = null
    } catch (failure) {
      if (own !== request) return

      error.value = failure
    } finally {
      if (own === request) isLoading.value = false
    }
  }

  /**
   * Записать заметку. Список обновляется ответом сервера, а не своей копией:
   * идентификатор и время создания ставит он, а без идентификатора карточку
   * нечем ни разобрать, ни удалить.
   */
  const add = async (body: string): Promise<boolean> => {
    const text = body.trim()

    if (!text) return false

    try {
      const note = await createNote(text, sessionId.value)

      if (note) items.value = [note, ...items.value]

      return !!note
    } catch (failure) {
      // Об отказе обязан узнать вызывающий: набранный текст держит он, и
      // очистить поле, не сохранив, значит потерять сказанное на созвоне.
      error.value = failure

      return false
    }
  }

  const replace = (note: Note) => {
    items.value = items.value.map((item) => (item.id === note.id ? note : item))
  }

  /** Заметка разобрана: ключи вышедших из неё задач остаются в ней навсегда */
  const markProcessed = async (id: string, issueKeys: string[]) => {
    try {
      const note = await updateNote(id, { processed: true, issueKeys })

      if (note) replace(note)
    } catch (failure) {
      error.value = failure
    } finally {
      if (parsingId.value === id) parsingId.value = ''
    }
  }

  /**
   * Решение по карточке действия из расшифровки.
   *
   * Задачу заводит человек кнопкой — модель только предложила (решение 5
   * гриля). Отклонение обратимо: случайный клик иначе уносил бы действие и из
   * переноса в следующий созвон серии.
   */
  const setAiState = async (id: string, state: string, issueKeys: string[] = []) => {
    try {
      const note = await updateNote(id, { aiState: state, issueKeys })

      if (note) replace(note)
    } catch (failure) {
      error.value = failure
    }
  }

  const remove = async (id: string) => {
    const saved = items.value

    items.value = items.value.filter((item) => item.id !== id)

    try {
      await deleteNote(id)
    } catch (failure) {
      // На встрече заметку мог снести сосед секундой раньше: «её нет» — это
      // ровно то, чего мы добивались, и возвращать её на экран незачем.
      if (isGone(failure)) return

      items.value = saved
      error.value = failure
    }
  }

  /** Правка заметки: на встрече текст поправляют прямо в карточке */
  const edit = async (id: string, body: string): Promise<boolean> => {
    const text = body.trim()

    if (!text) return false

    try {
      const note = await updateNote(id, { body: text })

      if (note) replace(note)

      return !!note
    } catch (failure) {
      error.value = failure

      return false
    }
  }

  /**
   * Показать ленту одной встречи или вернуться к личной.
   *
   * Та же сессия второй раз ленту не перечитывает — кроме самого первого раза:
   * экран «Встречи» открывается на личных заметках, то есть с пустой сессией,
   * и без этой оговорки лента при заходе не грузилась бы вовсе.
   */
  const setSession = async (id: string) => {
    if (sessionId.value === id && isLoaded.value) return

    sessionId.value = id
    await load()
  }

  const assistant = useAssistantStore()

  /**
   * Вызовы, уже учтённые при пометке.
   *
   * Список панели перечитывается при каждом возврате в разговор, и без этого
   * одни и те же задачи вписывались бы в заметку по кругу.
   */
  const counted = new Set<string>()

  const startParsing = (id: string) => {
    parsingId.value = id

    // Всё, что ассистент завёл до нажатия, к этой заметке отношения не имеет:
    // разговор мог начаться задолго до неё и с другой задачи.
    for (const call of createCalls(assistant.items)) counted.add(call.callId)
  }

  /**
   * Ассистент договорил — заметка разобрана.
   *
   * Пометку ставит фронт, а не бэкенд: инструмент создания задач ничего не
   * знает про заметки, и учить его этому значило бы связать «создай задачи» с
   * «закрой заметку» навсегда — включая случаи, когда задачи создают без неё.
   *
   * Наблюдатель живёт в сторе, а не в ленте заметок: между кнопкой «В задачи» и
   * созданной задачей проходит разговор в несколько ходов, и размонтированная
   * лента — человек ушёл на доску, пока ассистент думает, — потеряла бы его
   * конец.
   *
   * Считается конец хода, а не каждый вызов: пометка перезаписывает ключи
   * заметки целиком, и вторая задача, заведённая своим вызовом, стёрла бы
   * первую.
   */
  watch(
    () => assistant.isStreaming,
    (streaming) => {
      const id = parsingId.value

      if (streaming || !id) return

      const keys: string[] = []

      for (const call of createCalls(assistant.items)) {
        if (counted.has(call.callId)) continue

        counted.add(call.callId)
        keys.push(...issueKeysOf(call.result))
      }

      if (keys.length) void markProcessed(id, keys)
    },
  )

  return {
    items,
    fresh,
    sessionId,
    total,
    isTruncated,
    isLoading,
    isLoaded,
    error,
    parsingId,
    load,
    add,
    markProcessed,
    setAiState,
    remove,
    edit,
    setSession,
    startParsing,
  }
})

/** Ответ «такой заметки нет»: её уже удалили, и это не ошибка нашего экрана */
const isGone = (failure: unknown): boolean =>
  (failure as { response?: { status?: number } })?.response?.status === 404
